import { FileType } from './fileService';

export async function readImageAsDataUrl(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
}

export interface FileInfo {
  type: string;
  size: number;
}

export function useFileValidator() {

  const toMb = (size: number) => {
    return size / 1024 / 1024;
  };

  const fromMb = (size: number) => {
    return size * 1024 * 1024;
  };

  const validateFileSize = async (file: FileInfo, maxSize: number) => {
    if (file.size > maxSize) {
      return [
        `File size exceeds limit of ${toMb(maxSize)} Mb`,
      ];
    } else {
      return [];
    }
  };

  const validateMimeType = async (
    file: FileInfo,
    allowedMimeTypes: string[],
    errorMessage?: string,
  ) => {
    if (!allowedMimeTypes.includes(file.type)) {
      if (errorMessage) {
        return [errorMessage];
      } else {
        return [
          `File type ${file.type} is not allowed. Allowed files: ${allowedMimeTypes.join(', ')}`,
        ];
      }
    } else {
      return [];
    }
  };

  const validateImageSize = async (file: FileInfo, minWidth?: number, minHeight?: number): Promise<string[]> => {
    const fileData = await readImageAsDataUrl(file as File);
    return new Promise((resolve, reject) => {
      if (minHeight || minWidth) {
        const image = new Image();

        // Set up an event listener for when the Image has finished loading
        image.onload = () => {
          // Access the dimensions of the loaded image
          const width = image.width;
          const height = image.height;
          const errors: string[] = [];

          if (minWidth && minWidth > width) {
            errors.push(
              `Please ensure that your image has a width of at least ${minWidth} pixels.`,
            );
          }

          if (minHeight && minHeight > height) {
            errors.push(
              `Please ensure that your image has a height of at least ${minHeight} pixels.`,
            );
          }

          resolve(errors);
        };

        image.onerror = reject;

        // Set the source of the Image object as the result of the FileReader
        image.src = fileData;
      } else {
        resolve([]);
      }
    });
  };

  const validateFile = async (file: FileInfo, fileType: FileType) => {
    if (fileType === FileType.PROJECT_DOCUMENT) {
      return [
        ...await validateFileSize(file, fromMb(25)),
        ...await validateMimeType(
          file,
          [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.oasis.opendocument.text',
            'application/rtf',
          ],
          'Only PDF and Microsoft Word files are allowed.',
        ),
      ];

    } else if (fileType === FileType.PROJECT_PHOTO) {
      return [
        ...await validateFileSize(file, fromMb(5)),
        ...await validateMimeType(
          file,
          [
            'image/jpeg',
            'image/png',
          ],
          'Only JPEG and PNG files are allowed.',
        ),
        ...await validateImageSize(file as FileInfo, 1000, 450),
      ];
    } else {
      return [];
    }
  };

  return {
    validateFile,
    validateMimeType,
  };
}
